/* Court Records — the public courtroom gallery. Every verdict across every world:
   docket, charges, outcome, fines, custody, presiding judge. Justice, on display. */
import { useEffect, useState } from "react";
import { Scale, Gavel, MapPin, CalendarDays, UserRound } from "lucide-react";
import api from "@/lib/api";
import SiteNav from "@/components/SiteNav";

const OUTCOME_CLS = { guilty: "border-red-500/50 text-red-400 bg-red-500/[0.06]", not_guilty: "border-emerald-500/50 text-emerald-400 bg-emerald-500/[0.06]" };

export default function CourtRecordsPage() {
  const [records, setRecords] = useState(null);

  useEffect(() => {
    api.get("/nc/court/records").then((r) => setRecords(r.data.records)).catch(() => setRecords([]));
  }, []);

  return (
    <div data-testid="court-records-page" className="min-h-screen bg-[#05070a] text-zinc-100">
      <SiteNav />
      <div className="max-w-4xl mx-auto px-6 py-14">
        <p className="font-mono text-[10px] tracking-[0.4em] uppercase text-nc-blue flex items-center gap-2">
          <Scale className="w-4 h-4" /> Bornwood Courthouse — public record
        </p>
        <h1 className="font-bold uppercase tracking-wide text-2xl sm:text-4xl mt-3 leading-tight">Court Records</h1>
        <p className="font-mono text-xs text-zinc-400 mt-4 max-w-2xl leading-relaxed">
          Every verdict the bench has delivered, across every world. The gallery is always open —
          watch the courts, learn the law, stay out of the docket.
        </p>

        {records === null && <p className="font-mono text-xs text-zinc-500 mt-12 animate-pulse">Pulling the dockets…</p>}
        {records?.length === 0 && (
          <div className="border border-dashed border-white/15 p-12 text-center mt-12">
            <Scale className="w-8 h-8 text-zinc-600 mx-auto" />
            <p className="font-mono text-xs text-zinc-400 mt-4">No verdicts on record yet — the bench awaits its first case.</p>
          </div>
        )}

        <div className="mt-10 flex flex-col gap-3">
          {(records || []).map((r) => (
            <article key={r.id} data-testid={`court-record-${r.id}`} className="border border-white/10 bg-black/40 px-5 py-4">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-[10px] font-bold text-zinc-100">{r.docket}</span>
                <span className={`font-mono text-[8px] uppercase tracking-[0.15em] border px-2 py-0.5 ${OUTCOME_CLS[r.outcome] || "border-white/15 text-zinc-400"}`}>
                  {(r.outcome || "").replace("_", " ")}
                </span>
                {r.appeal_status && <span className="font-mono text-[8px] uppercase tracking-[0.15em] border border-sky-500/40 text-sky-300 px-2 py-0.5">appeal {r.appeal_status}</span>}
                <span className="ml-auto font-mono text-[8px] text-zinc-600 uppercase tracking-[0.15em] flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> {r.server_name} · <CalendarDays className="w-3 h-3" /> {new Date(r.at).toLocaleDateString()}
                </span>
              </div>
              <p className="font-mono text-[10px] text-zinc-400 mt-2 flex items-center gap-2 flex-wrap">
                <UserRound className="w-3 h-3 text-zinc-600" /> {r.defendant} — {r.charges.join(" · ") || "charges sealed"}
              </p>
              <p className="font-mono text-[10px] text-zinc-500 mt-1.5 flex items-center gap-2 flex-wrap">
                <Gavel className="w-3 h-3 text-zinc-600" />
                {r.fine > 0 && <span className="text-amber-300">${r.fine.toLocaleString()} fine</span>}
                {r.jail_seconds > 0 && <span className="text-red-400">{r.jail_seconds}s custody</span>}
                {r.judge && <span className="text-zinc-500">before Judge {r.judge}</span>}
                {r.notes && <span className="text-zinc-600 italic">"{r.notes}"</span>}
              </p>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}
